// Pure phase resolver for PowerSync detail pages with optional tRPC fallback.
// Used after create/upload → navigate when the SQLite row may still be missing.

export type SyncedDetailPhase = "loading" | "ready" | "not_found" | "load_failed" | "sync_pending";

export type RemoteStatus = "idle" | "pending" | "success" | "not_found" | "error";

export interface ResolveSyncedDetailPhaseInput {
	/** Entity present in PowerSync or from successful remote/seed. */
	hasData: boolean;
	/** Grace window still open (`useAwaitingSync`). */
	awaitingSync: boolean;
	/** Primary PowerSync sync completed at least once. */
	hasSynced: boolean;
	isOnline: boolean;
	remoteStatus: RemoteStatus;
	/** PowerSync SQL error (rare). */
	localError?: boolean;
}

/**
 * Resolves UI phase for a PowerSync-primary detail page.
 *
 * Priority:
 * 1. hasData → ready (local wins over remote)
 * 2. awaitingSync or remote pending → loading
 * 3. offline + no data after grace → sync_pending
 * 4. remote NOT_FOUND after grace → not_found (even if hasSynced is false)
 * 5. remote/network/local error after grace → load_failed
 * 6. otherwise loading (still waiting for first signal)
 */
export function resolveSyncedDetailPhase(input: ResolveSyncedDetailPhaseInput): SyncedDetailPhase {
	const { hasData, awaitingSync, hasSynced, isOnline, remoteStatus, localError } = input;

	if (hasData) return "ready";

	if (awaitingSync || remoteStatus === "pending") return "loading";

	if (!isOnline) return "sync_pending";

	// Explicit server NOT_FOUND after grace — trust it even if hasSynced is still
	// false (stuck PowerSync). Create→navigate races are covered by awaitingSync
	// + seed; a committed row won't 404 from materials.get/sets.get.
	if (remoteStatus === "not_found") return "not_found";

	if (remoteStatus === "error" || localError) return "load_failed";

	// Online, grace expired, remote idle/success without data (shouldn't happen
	// for success) — treat as not_found only after hasSynced, else keep loading.
	if (hasSynced && (remoteStatus === "idle" || remoteStatus === "success")) {
		return "not_found";
	}

	return "loading";
}
